import type { KeyProvider } from '@traceio-org/platform-contracts';

/*
 * Provider logos of the Keys page, vendored as SVG path data (no dependency, no request).
 *
 * DigitalOcean, Stripe, Resend, Auth0, Anthropic, OpenAI, Google Maps, Mapbox, Twilio, Sentry, ngrok and PostgreSQL:
 * Simple Icons 13.21.0 (https://simpleicons.org, icons/<slug>.svg), CC0 1.0 Universal, public domain dedication. The brand colors are the ones Simple Icons publishes with each icon. The marks
 * remain trademarks of their owners and are shown only to name which provider issued a key.
 *
 * Trace: the official Trace logomark (the same path as the Admin brand mark). AgentMail, Reducto, ExchangeRate-API, JEV
 * and a provider staff named themselves (other) have no Simple Icons entry: a plain globe tile with the provider's
 * name (GlobeGlyph, drawn here).
 */

export interface ProviderLogo {
  /** The provider's name, as the page says it. */
  name: string;
  viewBox: string;
  /** The mark's path, or null for a provider without one (a globe tile). */
  path: string | null;
  /** True when the provider itself expires its keys (the dialog says expiry); else a rotation date staff choose. */
  expires: boolean;
  /** The tile behind the mark and the mark's color. */
  background: string;
  ink: string;
  /** Where its keys are made and revoked (How to rotate), or empty when there is no one page for it. */
  consoleUrl: string;
  consoleName: string;
}

const DIGITALOCEAN_PATH =
  'M12.04 0C5.408-.02.005 5.37.005 11.992h4.638c0-4.923 4.882-8.731 10.064-6.855a6.95 6.95 0 014.147 4.148c1.889 5.177-1.924 10.055-6.84 10.064v-4.61H7.391v4.623h4.61V24c7.86 0 13.967-7.588 11.397-15.83-1.115-3.59-3.985-6.446-7.575-7.575A12.8 12.8 0 0012.039 0zM7.39 19.362H3.828v3.564H7.39zm-3.563 0v-2.978H.85v2.978z';

export const PROVIDER_LOGOS: Readonly<Record<KeyProvider, ProviderLogo>> = {
  digitalocean: {
    name: 'DigitalOcean',
    viewBox: '0 0 24 24',
    path: DIGITALOCEAN_PATH,
    background: '#0080ff',
    ink: '#ffffff',
    expires: true,
    consoleUrl: 'https://cloud.digitalocean.com/account/api/tokens',
    consoleName: 'DigitalOcean, API tokens',
  },
  digitalocean_spaces: {
    name: 'DigitalOcean Spaces',
    viewBox: '0 0 24 24',
    path: DIGITALOCEAN_PATH,
    background: '#0080ff',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://cloud.digitalocean.com/spaces/access_keys',
    consoleName: 'DigitalOcean, Spaces access keys',
  },
  stripe: {
    name: 'Stripe',
    viewBox: '0 0 24 24',
    path: 'M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z',
    background: '#635bff',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://dashboard.stripe.com/apikeys',
    consoleName: 'Stripe, API keys',
  },
  resend: {
    name: 'Resend',
    viewBox: '0 0 24 24',
    path: 'M2.023 0v24h5.553v-8.434h2.998L15.326 24h6.65l-5.372-9.258a7.652 7.652 0 0 0 3.316-3.016c.709-1.21 1.062-2.57 1.062-4.08 0-1.462-.353-2.767-1.062-3.91-.709-1.165-1.692-2.079-2.95-2.742C15.737.331 14.355 0 12.823 0Zm5.553 4.87h4.219c.731 0 1.349.125 1.851.376.526.252.925.618 1.2 1.098.274.457.412.994.412 1.611S15.132 9.12 14.88 9.6c-.229.48-.572.856-1.03 1.13-.434.252-.948.38-1.542.38H7.576Z',
    background: '#000000',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://resend.com/api-keys',
    consoleName: 'Resend, API keys',
  },
  auth0: {
    name: 'Auth0',
    viewBox: '0 0 24 24',
    path: 'M21.98 7.448L19.62 0H4.347L2.02 7.448c-1.352 4.312.03 9.206 3.815 12.015L12.007 24l6.157-4.552c3.755-2.81 5.182-7.688 3.815-12.015l-6.16 4.58 2.343 7.45-6.157-4.597-6.158 4.58 2.358-7.433-6.188-4.55 7.63-.045L12.008 0l2.356 7.404 7.615.044z',
    background: '#eb5424',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://manage.auth0.com/',
    consoleName: 'the Auth0 dashboard, Applications',
  },
  anthropic: {
    name: 'Anthropic',
    viewBox: '0 0 24 24',
    path: 'M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z',
    background: '#191919',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://console.anthropic.com/settings/keys',
    consoleName: 'the Anthropic Console, API keys',
  },
  openai: {
    name: 'OpenAI',
    viewBox: '0 0 24 24',
    path: 'M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z',
    background: '#412991',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://platform.openai.com/api-keys',
    consoleName: 'the OpenAI platform, API keys',
  },
  google_maps: {
    name: 'Google Maps',
    viewBox: '0 0 24 24',
    path: 'M19.527 4.799c1.212 2.608.937 5.678-.405 8.173-1.101 2.047-2.744 3.74-4.098 5.614-.619.858-1.244 1.75-1.669 2.727-.141.325-.263.658-.383.992-.121.333-.224.673-.34 1.008-.109.314-.236.684-.627.687h-.007c-.466-.001-.579-.53-.695-.887-.284-.874-.581-1.713-1.019-2.525-.51-.944-1.145-1.817-1.79-2.671L19.527 4.799zM8.545 7.705l-3.959 4.707c.724 1.54 1.821 2.863 2.871 4.18.247.31.494.622.737.936l4.984-5.925-.029.01c-1.741.601-3.691-.291-4.392-1.987a3.377 3.377 0 0 1-.209-.716c-.063-.437-.077-.761-.004-1.198l.001-.007zM5.492 3.149l-.003.004c-1.947 2.466-2.281 5.88-1.117 8.77l4.785-5.689-.058-.05-3.607-3.035zM14.661.436l-3.838 4.563a.295.295 0 0 1 .027-.01c1.6-.551 3.403.15 4.22 1.626.176.319.323.683.377 1.045.068.446.085.773.012 1.22l-.003.016 3.836-4.561A8.382 8.382 0 0 0 14.67.439l-.009-.003zM9.466 5.868L14.162.285l-.047-.012A8.31 8.31 0 0 0 11.986 0a8.439 8.439 0 0 0-6.169 2.766l-.016.018 3.665 3.084z',
    background: '#4285f4',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://console.cloud.google.com/google/maps-apis/credentials',
    consoleName: 'Google Cloud, Maps credentials',
  },
  mapbox: {
    name: 'Mapbox',
    viewBox: '0 0 24 24',
    path: 'M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm5.696 14.943c-4.103 4.103-11.433 2.794-11.433 2.794S4.94 10.421 9.057 6.304c2.281-2.281 6.061-2.187 8.45.189s2.471 6.168.189 8.45zm-4.319-7.91l-1.174 2.416-2.416 1.174 2.416 1.174 1.174 2.416 1.174-2.416 2.416-1.174-2.416-1.174-1.174-2.416z',
    background: '#000000',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://console.mapbox.com/account/access-tokens/',
    consoleName: 'Mapbox, access tokens',
  },
  twilio: {
    name: 'Twilio',
    viewBox: '0 0 24 24',
    path: 'M12 0C5.381-.008.008 5.352 0 11.971V12c0 6.64 5.359 12 12 12 6.64 0 12-5.36 12-12 0-6.641-5.36-12-12-12zm0 20.801c-4.846.015-8.786-3.904-8.801-8.75V12c-.014-4.846 3.904-8.786 8.75-8.801H12c4.847-.014 8.786 3.904 8.801 8.75V12c.015 4.847-3.904 8.786-8.75 8.801H12zm5.44-11.76c0 1.359-1.12 2.479-2.481 2.479-1.366-.007-2.472-1.113-2.479-2.479 0-1.361 1.12-2.481 2.479-2.481 1.361 0 2.481 1.12 2.481 2.481zm0 5.919c0 1.36-1.12 2.48-2.481 2.48-1.367-.008-2.473-1.114-2.479-2.48 0-1.359 1.12-2.479 2.479-2.479 1.361-.001 2.481 1.12 2.481 2.479zm-5.919 0c0 1.36-1.12 2.48-2.479 2.48-1.368-.007-2.475-1.113-2.481-2.48 0-1.359 1.12-2.479 2.481-2.479 1.358-.001 2.479 1.12 2.479 2.479zm0-5.919c0 1.359-1.12 2.479-2.479 2.479-1.367-.007-2.475-1.112-2.481-2.479 0-1.361 1.12-2.481 2.481-2.481 1.358 0 2.479 1.12 2.479 2.481z',
    background: '#f22f46',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://console.twilio.com/',
    consoleName: 'the Twilio Console',
  },
  sentry: {
    name: 'Sentry',
    viewBox: '0 0 24 24',
    path: 'M13.91 2.505c-.873-1.448-2.972-1.448-3.844 0L6.904 7.92a15.478 15.478 0 0 1 8.53 12.811h-2.221A13.301 13.301 0 0 0 5.784 9.814l-2.926 5.06a7.65 7.65 0 0 1 4.435 5.848H2.194a.365.365 0 0 1-.298-.534l1.413-2.402a5.16 5.16 0 0 0-1.614-.913L.296 19.275a2.182 2.182 0 0 0 .812 2.999 2.24 2.24 0 0 0 1.086.288h6.983a9.322 9.322 0 0 0-3.845-8.318l1.11-1.922a11.47 11.47 0 0 1 4.95 10.24h5.915a17.242 17.242 0 0 0-7.885-15.28l2.244-3.845a.37.37 0 0 1 .504-.13c.255.14 9.75 16.708 9.928 16.9a.365.365 0 0 1-.327.543h-2.287c.029.612.029 1.223 0 1.831h2.297a2.206 2.206 0 0 0 1.922-3.31z',
    background: '#362d59',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://sentry.io/settings/account/api/auth-tokens/',
    consoleName: 'Sentry, auth tokens',
  },
  ngrok: {
    name: 'ngrok',
    viewBox: '0 0 24 24',
    path: 'M18.951 7.598v6.713h1.463v-1.69l1.61 1.69H24v-.08l-2.123-2.153 2.002-1.846v-.07H21.95l-1.537 1.496v-4.06zm-2.93 2.41a2.626 2.626 0 00-1.787.635 2.049 2.049 0 00-.703 1.556c-.002.75.311 1.287.7 1.643.526.478 1.221.626 1.767.623.666 0 1.34-.195 1.805-.62.521-.483.713-1.029.713-1.607 0-.73-.31-1.247-.71-1.603-.553-.475-1.202-.628-1.785-.627zm-9.062.039c-1.188-.005-2.1.977-2.104 2.25-.004 1.296.908 2.108 2.032 2.096.664.002.983-.244 1.308-.541v1.193h-1.37l-1.03 1.158v.2H9.66v-6.24H8.195v.435c-.381-.408-.772-.542-1.236-.551zm-4.805.11l-.691.786v-.771H0v4.15h1.463v-2.799c.547.002 1.023-.002 1.49-.003v2.802h1.465v-2.595c-.004-.547-.1-.819-.307-1.061a1.431 1.431 0 00-.914-.51zm8.114.005v4.15h1.468l.002-2.779h1.065l1.164-1.314v-.057h-1.598l-.635.715v-.715zm-2.946 1.115c.504 0 .96.444.948.948a.956.956 0 01-.948.945c-.523 0-.931-.403-.947-.945-.002-.52.443-.94.947-.948zm8.703.001c.525 0 .94.434.944.95-.027.544-.42.95-.944.95s-.934-.417-.95-.95a.955.955 0 01.95-.95z',
    background: '#1f1e37',
    ink: '#ffffff',
    expires: false,
    consoleUrl: 'https://dashboard.ngrok.com/get-started/your-authtoken',
    consoleName: 'the ngrok dashboard, authtoken',
  },
  postgresql: {
    name: 'PostgreSQL',
    viewBox: '0 0 24 24',
    path: 'M23.5594 14.7228a.5269.5269 0 0 0-.0563-.1191c-.139-.2632-.4768-.3418-1.0074-.2321-1.6533.3411-2.2935.1312-2.5256-.0191 1.342-2.0482 2.445-4.522 3.0411-6.8297.2714-1.0507.7982-3.5237.1222-4.7316a1.5641 1.5641 0 0 0-.1509-.235C21.6931.9086 19.8007.0248 17.5099.0005c-1.4947-.0158-2.7705.3461-3.1161.4794a9.449 9.449 0 0 0-.5159-.0816 8.044 8.044 0 0 0-1.3114-.1278c-1.1822-.0184-2.2038.2642-3.0498.8406-.8573-.3211-4.7888-1.645-7.2219.0788C.9359 2.1526.3086 3.8733.4302 6.3043c.0409.818.5069 3.334 1.2423 5.7436.4598 1.5065.9387 2.7019 1.4334 3.582.553.9942 1.1259 1.5933 1.7143 1.7895.4474.1491 1.1327.1441 1.8581-.7279.8012-.9635 1.5903-1.8258 1.9446-2.2069.4351.2355.9064.3625 1.39.3772a.0569.0569 0 0 0 .0004.0041 11.0312 11.0312 0 0 0-.2472.3054c-.3389.4302-.4094.5197-1.5002.7443-.3102.064-1.1344.2339-1.1464.8115-.0025.1224.0329.2309.0919.3268.2269.4231.9216.6097 1.015.6331 1.3345.3335 2.5044.092 3.3714-.6787-.017 2.231.0775 4.4174.3454 5.0874.2212.5529.7618 1.9045 2.4692 1.9043.2505 0 .5263-.0291.8296-.0941 1.7819-.3821 2.5557-1.1696 2.855-2.9059.1503-.8707.4016-2.8753.5388-4.1012.0169-.0703.0357-.1207.057-.1362.0007-.0005.0697-.0471.4272.0307a.3673.3673 0 0 0 .0443.0068l.2539.0223.0149.001c.8468.0384 1.9114-.1426 2.5312-.4308.6438-.2988 1.8057-1.0323 1.5951-1.6698zM2.371 11.8765c-.7435-2.4358-1.1779-4.8851-1.2123-5.5719-.1086-2.1714.4171-3.6829 1.5623-4.4927 1.8367-1.2986 4.8398-.5408 6.108-.13-.0032.0032-.0066.0061-.0098.0094-2.0238 2.044-1.9758 5.536-1.9708 5.7495-.0002.0823.0066.1989.0162.3593.0348.5873.0996 1.6804-.0735 2.9184-.1609 1.1504.1937 2.2764.9728 3.0892.0806.0841.1648.1631.2518.2374-.3468.3714-1.1004 1.1926-1.9025 2.1576-.5677.6825-.9597.5517-1.0886.5087-.3919-.1307-.813-.5871-1.2381-1.3223-.4796-.839-.9635-2.0317-1.4155-3.5126zm6.0072 5.0871c-.1711-.0428-.3271-.1132-.4322-.1772.0889-.0394.2374-.0902.4833-.1409 1.2833-.2641 1.4815-.4506 1.9143-1.0002.0992-.126.2116-.2687.3673-.4426a.3549.3549 0 0 0 .0737-.1298c.1708-.1513.2724-.1099.4369-.0417.156.0646.3078.26.3695.4752.0291.1016.0619.2945-.0452.4444-.9043 1.2658-2.2216 1.2494-3.1676 1.0128zm2.094-3.988-.0525.141c-.133.3566-.2567.6881-.3334 1.003-.6674-.0021-1.3168-.2872-1.8105-.8024-.6279-.6551-.9131-1.5664-.7825-2.5004.1828-1.3079.1153-2.4468.079-3.0586-.005-.0857-.0095-.1607-.0122-.2199.2957-.2621 1.6659-.9962 2.6429-.7724.4459.1022.7176.4057.8305.928.5846 2.7038.0774 3.8307-.3302 4.7363-.084.1866-.1633.3629-.2311.5454zm7.3637 4.5725c-.0169.1768-.0358.376-.0618.5959l-.146.4383a.3547.3547 0 0 0-.0182.1077c-.0059.4747-.054.6489-.115.8693-.0634.2292-.1353.4891-.1794 1.0575-.11 1.4143-.8782 2.2267-2.4172 2.5565-1.5155.3251-1.7843-.4968-2.0212-1.2217a6.5824 6.5824 0 0 0-.0769-.2266c-.2154-.5858-.1911-1.4119-.1574-2.5551.0165-.5612-.0249-1.9013-.3302-2.6462.0044-.2932.0106-.5909.019-.8918a.3529.3529 0 0 0-.0153-.1126 1.4927 1.4927 0 0 0-.0439-.208c-.1226-.4283-.4213-.7866-.7797-.9351-.1424-.059-.4038-.1672-.7178-.0869.067-.276.1831-.5875.309-.9249l.0529-.142c.0595-.16.134-.3257.213-.5012.4265-.9476 1.0106-2.2453.3766-5.1772-.2374-1.0981-1.0304-1.6343-2.2324-1.5098-.7207.0746-1.3799.3654-1.7088.5321a5.6716 5.6716 0 0 0-.1958.1041c.0918-1.1064.4386-3.1741 1.7357-4.4823a4.0306 4.0306 0 0 1 .3033-.276.3532.3532 0 0 0 .1447-.0644c.7524-.5706 1.6945-.8506 2.802-.8325.4091.0067.8017.0339 1.1742.081 1.939.3544 3.2439 1.4468 4.0359 2.3827.8143.9623 1.2552 1.9315 1.4312 2.4543-1.3232-.1346-2.2234.1268-2.6797.779-.9926 1.4189.543 4.1729 1.2811 5.4964.1353.2426.2522.4522.2889.5413.2403.5825.5515.9713.7787 1.2552.0696.087.1372.1714.1885.245-.4008.1155-1.1208.3825-1.0552 1.717-.0123.1563-.0423.4469-.0834.8148-.0461.2077-.0702.4603-.0994.7662zm.8905-1.6211c-.0405-.8316.2691-.9185.5967-1.0105a2.8566 2.8566 0 0 0 .135-.0406 1.202 1.202 0 0 0 .1342.103c.5703.3765 1.5823.4213 3.0068.1344-.2016.1769-.5189.3994-.9533.6011-.4098.1903-1.0957.333-1.7473.3636-.7197.0336-1.0859-.0807-1.1721-.151zm.5695-9.2712c-.0059.3508-.0542.6692-.1054 1.0017-.055.3576-.112.7274-.1264 1.1762-.0142.4368.0404.8909.0932 1.3301.1066.887.216 1.8003-.2075 2.7014a3.5272 3.5272 0 0 1-.1876-.3856c-.0527-.1276-.1669-.3326-.3251-.6162-.6156-1.1041-2.0574-3.6896-1.3193-4.7446.3795-.5427 1.3408-.5661 2.1781-.463zm.2284 7.0137a12.3762 12.3762 0 0 0-.0853-.1074l-.0355-.0444c.7262-1.1995.5842-2.3862.4578-3.4385-.0519-.4318-.1009-.8396-.0885-1.2226.0129-.4061.0666-.7543.1185-1.0911.0639-.415.1288-.8443.1109-1.3505.0134-.0531.0188-.1158.0118-.1902-.0457-.4855-.5999-1.938-1.7294-3.253-.6076-.7073-1.4896-1.4972-2.6889-2.0395.5251-.1066 1.2328-.2035 2.0244-.1859 2.0515.0456 3.6746.8135 4.8242 2.2824a.908.908 0 0 1 .0667.1002c.7231 1.3556-.2762 6.2751-2.9867 10.5405zm-8.8166-6.1162c-.025.1794-.3089.4225-.6211.4225a.5821.5821 0 0 1-.0809-.0056c-.1873-.026-.3765-.144-.5059-.3156-.0458-.0605-.1203-.178-.1055-.2844.0055-.0401.0261-.0985.0925-.1488.1182-.0894.3518-.1226.6096-.0867.3163.0441.6426.1938.6113.4186zm7.9305-.4114c.0111.0792-.049.201-.1531.3102-.0683.0717-.212.1961-.4079.2232a.5456.5456 0 0 1-.075.0052c-.2935 0-.5414-.2344-.5607-.3717-.024-.1765.2641-.3106.5611-.352.297-.0414.6111.0088.6356.1851z',
    background: '#4169e1',
    ink: '#ffffff',
    expires: false,
    consoleUrl: '',
    consoleName: '',
  },
  agentmail: {
    name: 'AgentMail',
    viewBox: '0 0 24 24',
    path: null,
    background: '',
    ink: '',
    expires: false,
    consoleUrl: 'https://console.agentmail.to/',
    consoleName: 'the AgentMail console',
  },
  reducto: {
    name: 'Reducto',
    viewBox: '0 0 24 24',
    path: null,
    background: '',
    ink: '',
    expires: false,
    consoleUrl: 'https://studio.reducto.ai/',
    consoleName: 'Reducto Studio',
  },
  exchangerate_api: {
    name: 'ExchangeRate-API',
    viewBox: '0 0 24 24',
    path: null,
    background: '',
    ink: '',
    expires: false,
    consoleUrl: 'https://app.exchangerate-api.com/',
    consoleName: 'the ExchangeRate-API dashboard',
  },
  jev: {
    name: 'JEV',
    viewBox: '0 0 24 24',
    path: null,
    background: '',
    ink: '',
    expires: false,
    consoleUrl: '',
    consoleName: '',
  },
  other: {
    name: 'Another provider',
    viewBox: '0 0 24 24',
    path: null,
    background: '',
    ink: '',
    expires: false,
    consoleUrl: '',
    consoleName: '',
  },
  trace: {
    name: 'Trace (our own)',
    viewBox: '0 0 103.3 102.6',
    path: 'M51.72,41.37c-4.17,0-6.48-4.82-3.88-8.07l4.49-5.6c6.3-7.86,9.73-17.63,9.73-27.7h-20.69v11.81c0,16.33-13.24,29.56-29.58,29.56H0v17.67h51.72c4.17,0,6.48,4.82,3.88,8.07l-4.49,5.6c-6.3,7.86-9.73,17.63-9.73,27.7h20.69v-11.81c0-16.33,13.24-29.56,29.58-29.56h11.81v-17.67h-51.72Z',
    background: '#1a1a1a',
    ink: '#e9eda4',
    expires: false,
    consoleUrl: '',
    consoleName: '',
  },
};

/** The provider's name as the page says it: staff's own words for other, else the logo's name, else none chosen. */
export function providerLabel(provider: KeyProvider | null, providerName: string | null = null): string {
  if (provider === 'other') return providerName ?? PROVIDER_LOGOS.other.name;
  return provider ? PROVIDER_LOGOS[provider].name : 'Provider not chosen';
}

/**
 * A key's tile: the provider's mark, a globe for a provider without one (named in the tooltip and beside the key), or
 * a plain key for a name no rule matched.
 */
export function ProviderTile({
  provider,
  providerName = null,
}: {
  provider: KeyProvider | null;
  providerName?: string | null;
}) {
  const logo = provider ? PROVIDER_LOGOS[provider] : null;
  const name = providerLabel(provider, providerName);
  return (
    <span
      className="admin-key-tile"
      role="img"
      aria-label={name}
      title={name}
      data-provider={provider ?? 'none'}
      style={logo?.path ? { background: logo.background } : undefined}
    >
      {logo?.path ? (
        <svg viewBox={logo.viewBox} width="14" height="14" aria-hidden="true">
          <path fill={logo.ink} d={logo.path} />
        </svg>
      ) : logo ? (
        <GlobeGlyph size={14} />
      ) : (
        <KeyGlyph size={14} />
      )}
    </span>
  );
}

/** A globe, drawn for Trace Admin: a provider with no mark of its own. Same stroke style as KeyGlyph. */
export function GlobeGlyph({ size = 20 }: { size?: number }) {
  return (
    <svg
      className="trace-icon-native icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
    </svg>
  );
}

/**
 * A key, drawn for Trace Admin (our own paths, no third-party icon): a ring, a shaft and two teeth, in the stroke
 * style of the navigation glyphs (24 px box, 1.65 stroke, currentColor).
 */
export function KeyGlyph({ size = 20 }: { size?: number }) {
  return (
    <svg
      className="trace-icon-native icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="8" cy="16" r="4.5" />
      <path d="M11.2 12.8 20 4" />
      <path d="M16.5 7.5 19 10" />
      <path d="M18.5 5.5 21 8" />
    </svg>
  );
}
